import {appClient, Scope} from '@wix/app-settings-client';
import {IWidgetControllerConfig} from '@wix/native-components-infra';
import {IFedOpsLogger} from '@wix/native-components-infra/dist/es/src/types/types';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {APP_DEFINITION_ID, APP_SETTINGS_CDN, StoresWidgetID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {BaseController} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/controller-factory/BaseController';
import {withErrorReporting} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/errorReporter';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import _ from 'lodash';
import {PRODUCT_PAGE_WIXCODE_APP_NAME, productPageFedopsEvent, UserInputType} from '../constants';
import {wixCodeGetProduct} from '../services/getProduct';
import {SPECS} from '../specs';
import {IProductPageControllerConfig} from '../types/app-types';
import {
  OnChoiceSelectedCallbackType,
  OnQuantityChangedCallbackType,
  OverrideCallback,
  ProductPageStore,
} from './ProductPageStore';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;

export class OldProductPageController extends BaseController {
  private productPageStore: ProductPageStore;
  private config: IProductPageControllerConfig;
  private readonly fedopsLogger: IFedOpsLogger;
  private readonly isResponsive: boolean;
  private readonly controllerConfig: IWidgetControllerConfig;
  private onReadyResolver: ResolveFn<void>;
  private readonly waitUntilReady: Promise<void>;

  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    const isMobile = this.siteStore.isMobile();
    const fedopsLoggerFactory = this.siteStore.platformServices.fedOpsLoggerFactory;
    this.fedopsLogger = fedopsLoggerFactory.getLoggerForWidget({
      appId: APP_DEFINITION_ID,
      widgetId: StoresWidgetID.PRODUCT_PAGE,
      appName: PRODUCT_PAGE_WIXCODE_APP_NAME,
    });
    /* istanbul ignore next: todo: test */
    this.isResponsive = controllerParams.controllerConfig.config.style.styleParams?.booleans?.responsive || isMobile;
    this.controllerConfig = controllerParams.controllerConfig;
    this.config = _.clone(controllerParams.controllerConfig.config);
    const isEditor = typeof window !== 'undefined' && window.Wix;

    this.waitUntilReady = new Promise<void>((resolve) => {
      this.onReadyResolver = resolve;
    });

    /* istanbul ignore else: todo(flow-editor): test */
    if (isEditor) {
      this.listenToAppSettingsUpdate();
    }
  }

  private listenToAppSettingsUpdate() {
    const appSettingsClient = appClient({scope: Scope.COMPONENT, cdnUrl: APP_SETTINGS_CDN});
    appSettingsClient.onChange((pb) => {
      this.productPageStore.updateState(this.config, {
        APP: undefined,
        COMPONENT: {},
        appSettings: pb,
      });
    });
  }

  public exports = () =>
    withErrorReporting(this.flowAPI.reportError)({
      getProduct: () => {
        const fedopsInteraction = productPageFedopsEvent.WixCodeGetProduct;
        this.fedopsLogger.interactionStarted(fedopsInteraction);
        this.flowAPI.panoramaClient.transaction(fedopsInteraction).start();
        const onSuccess = () => {
          this.fedopsLogger.interactionEnded(fedopsInteraction);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
        };
        return wixCodeGetProduct(this.siteStore, this.flowAPI.reportError, onSuccess);
      },
      getCustomTextFieldsValues: (): string[] => {
        const fedopsInteraction = productPageFedopsEvent.WixCodeGetCustomTextFieldValues;
        this.fedopsLogger.interactionStarted(fedopsInteraction);
        this.flowAPI.panoramaClient.transaction(fedopsInteraction).start();
        const customTextFieldsValues = this.productPageStore.userInputs.text;
        this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
        this.fedopsLogger.interactionEnded(fedopsInteraction);
        return customTextFieldsValues;
      },
      ...(this.siteStore.experiments.enabled(SPECS.PRODUCT_PAGE_NEW_WIX_CODE_API) && {
        // eslint-disable-next-line sonarjs/no-identical-functions
        getCustomText: async (): Promise<string[]> => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetCustomText;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).start();
          const customTextFieldsValues = this.productPageStore.customText;
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
          return customTextFieldsValues;
        },
        getQuantity: async () => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetQuantity;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          const selectedQuantity = this.productPageStore.selectedQuantity;
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
          return selectedQuantity;
        },
        setQuantity: async (value: number) => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeSetQuantity;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.productPageStore.handleUserInput(UserInputType.Quantity, value, 0);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
        },
        onQuantityChanged: async (callback: OnQuantityChangedCallbackType) => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnQuantityChanged;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.productPageStore.setOnQuantityChangedCallback(callback);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
        },
        getSelectedVariantId: async () => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetSelectedVariantId;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          const selectedVariantId = this.productPageStore.selectedVariant?.id;
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
          return selectedVariantId;
        },
        getSelectedChoices: async () => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeGetSelectedChoices;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          const selectedChoices = this.productPageStore.selectedChoices;
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
          return selectedChoices;
        },
        onChoiceSelected: async (callback: OnChoiceSelectedCallbackType) => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnChoiceSelected;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.productPageStore.setOnChoiceSelectedCallback(callback);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
        },
        onAddToCart: async (callback: OverrideCallback) => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnAddToCart;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.productPageStore.setOnAddToCartCallback(callback);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
        },
        setAddToCartLabel: async (text: string) => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnAddToCart;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.productPageStore.setAddToCartTextOverride(text);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
        },
        onBuyNow: async (callback: OverrideCallback) => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnBuyNow;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.productPageStore.setOnBuyNowCallback(callback);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
        },
        setBuyNowLabel: async (text: string) => {
          await this.waitUntilReady;
          const fedopsInteraction = productPageFedopsEvent.WixCodeOnBuyNow;
          this.fedopsLogger.interactionStarted(fedopsInteraction);
          this.productPageStore.setBuyNowTextOverride(text);
          this.flowAPI.panoramaClient.transaction(fedopsInteraction).finish();
          this.fedopsLogger.interactionEnded(fedopsInteraction);
        },
      }),
    });

  public onConfigUpdate = (config: IProductPageControllerConfig) => {
    const {publicData} = config;
    this.config = _.clone(config);
    return this.productPageStore.updateState(this.config, publicData);
  };

  /* istanbul ignore next: todo test */
  public onAppSettingsUpdate = (updates: {[key: string]: any}) => {
    if (
      this.siteStore.experiments.enabled(SPECS.EDITOR_OOI) &&
      updates.scope === Scope.COMPONENT &&
      updates.source === 'app-settings'
    ) {
      this.productPageStore.updateAppSettings(updates.payload);
    }
  };

  public getFreeTexts = (): string[] => {
    return [];
  };

  public readonly load = async () => {
    const {publicData} = this.config;
    if (this.productPageStore) {
      return;
    }

    this.productPageStore = new ProductPageStore(
      this.config,
      () => ({} as any),
      publicData,
      this.setProps.bind(this),
      this.siteStore,
      this.controllerConfig.config.externalId || '',
      this.flowAPI.reportError,
      this.controllerConfig.compId,
      createSlotVeloAPIFactory(this.controllerConfig),
      this.controllerConfig,
      this.flowAPI.errorHandler,
      this.flowAPI.panoramaClient
    );

    await this.productPageStore
      .setInitialState()
      .then(() => {
        this.onReadyResolver();
      })
      .catch(this.flowAPI.reportError);
  };

  public readonly init = async () => {
    await this.load();
  };
}
